<script setup lang="ts">
import { toRef } from 'vue';

import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDownloadReportButton from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import VDateRangeWithPresets from '@modules/reports/components/VDateRange/VDateRangeWithPresets.vue';
import VGroupByParams from '@modules/reports/components/VGroupByParams/VGroupByParams.vue';
import { TRANSLATION_GROUP_REPORTS } from '@modules/reports/constants';
import { ReportWidgetType } from '@modules/reports/enums';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import VTableMultiselectActions from '@modules/tables/components/VTableMultiselectActions/VTableMultiselectActions.vue';

import type { IReportsProps } from './types';
import VGroupBySortable from '../VGroupBySortable/VGroupBySortable.vue';
import VReportWidget from '../VReportWidget/VReportWidget.vue';
import { useReport } from './composables';
import { SEGMENTS_INNER_TABS, STRUCTURES_INNER_TABS } from './constants';

const props = defineProps<IReportsProps>();

const {
  tableState,
  tableData,
  isTableLoading,
  isTableFetching,
  isReportLoading,
  options,
  isOptionsLoading,
  reportTableVisible,
  isSortableGrouping,
  groupByValues,
  groupBySortableValues,
  groupBySortableSettings,
  availableGroupByNames,
  selectedRows,
  rowsPerPage,
  columnOptions,
  tableActions,
  subtotalEnabled,
  generateReport,
  updateTableState,
  onDownloadReport,
  onSelectSegment,
  onSelectStructure,
  onPeriodChange,
  onGroupParamsChange,
  onTableActionItemSelect,
  toggleSubtotalDisplay,
} = useReport(
  toRef(props, 'reportType'),
  toRef(props, 'generateReportPermission'),
  toRef(props, 'downloadReportPermission'),
  toRef(props, 'isStructuresAllowed'),
  toRef(props, 'translationKey'),
);
</script>

<template>
  <div v-data-qa-auto-id="'VReports'">
    <VCard
      :title="$getTranslation(translationKey, 'REPORTS.HEADER')"
      type="default"
    >
      <VDateRangeWithPresets
        :translation-key="translationKey"
        default-period-name="month"
        @change="onPeriodChange"
      />
      <VGroupByParams
        :translation-key="translationKey"
        :group-values="groupByValues"
        :group-options="options?.group_by"
        :available-groups="availableGroupByNames"
        :is-loading="isOptionsLoading"
        @change="onGroupParamsChange"
      />
      <VGroupBySortable
        v-if="isSortableGrouping"
        :translation-key="translationKey"
        :group-values="groupBySortableValues"
        :group-options="options?.group_by"
        :settings="groupBySortableSettings"
        :is-loading="isOptionsLoading"
        @change="onGroupParamsChange"
      />
      <VReportWidget
        class="mt-6"
        :report-type="reportType"
        :widget-type="ReportWidgetType.SEGMENTS"
        :query-permission="generateReportPermission"
        :translation-key="translationKey"
        :default-inner-tabs="SEGMENTS_INNER_TABS"
        @select-tab="onSelectSegment"
      />
      <VReportWidget
        v-if="isStructuresAllowed"
        class="mt-10"
        :report-type="reportType"
        :widget-type="ReportWidgetType.STRUCTURES"
        :query-permission="generateReportPermission"
        :structure-states-permission="structureStatesPermission"
        :translation-key="translationKey"
        :default-inner-tabs="STRUCTURES_INNER_TABS"
        @select-tab="onSelectStructure"
      />
      <div class="mt-10 flex gap-2 max-md:justify-center">
        <VButton
          v-if="$hasPermission(generateReportPermission)"
          :label="$getTranslation(translationKey, 'REPORTS.GENERATE_REPORT')"
          :loading="isTableFetching"
          @click="generateReport"
        />
        <VDownloadReportButton
          v-if="$hasPermission(downloadReportPermission)"
          :label="$getTranslation(translationKey, 'REPORTS.DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </div>
    </VCard>

    <VCard
      v-if="reportTableVisible"
      type="table"
      class="mt-4"
    >
      <VTable
        :data="tableData"
        :title-t-path="$getTPath(translationKey, 'REPORTS.RESULT_HEADER')"
        :total-label-t-path="$getTPath(translationKey, 'REPORTS.TOTAL_ENTRIES')"
        :subtotal-label-t-path="$getTPath(translationKey, 'REPORTS.SUB_TOTAL_ENTRIES')"
        :subtotal-enabled="subtotalEnabled"
        :loading="isTableLoading"
        :disable-all-controls="isTableFetching"
        :state="tableState"
        :rows-on-page="rowsPerPage"
        :selected-ids="selectedRows"
        :columns-options="columnOptions"
        :refresh-button="false"
        full-height
        @state-update="updateTableState"
      >
        <template
          v-if="tableActions && Object.keys(tableActions).length"
          #recordAction="{ listItem }"
        >
          <VTableMultiselectActions
            :list-item="listItem"
            :options="tableActions"
            :disabled="false"
            static
            @select="onTableActionItemSelect"
          />
        </template>
        <template #footerButtons>
          <VButton
            v-if="tableData?.sum_columns"
            :label="$getTranslation(TRANSLATION_GROUP_REPORTS, subtotalEnabled ? 'REPORTS.SUB_TOTAL_DISABLE' : 'REPORTS.SUB_TOTAL_ENABLE')"
            :severity="subtotalEnabled ? 'success' : 'danger'"
            @click="toggleSubtotalDisplay"
          />
        </template>
      </VTable>
    </VCard>

    <RouterView />
  </div>
</template>
